import { actionStatus as actionStatusTypes, ActionTypes } from 'queries/cases/types';
import { useTranslation } from 'react-i18next';
import { FormSelect, FormTextField } from 'ui/FormComponents';
import { ExternalReference } from '../FormFields/ExternalReference';
import { FormDatePicker } from '../FormFields/FormDatePicker';
import './actions.style.scss';
import dayjs from 'dayjs';
import { useFormContext } from 'react-hook-form';
import { Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useFeatureFlag } from '@shape/feature-flag';
import { TextEditorWithLabel } from 'components/TextEditorWithLabel/TextEditorWithLabel';

type Props = {
  readonly index?: number;
  readonly isDisabled?: boolean;
  readonly isStepRequired?: boolean;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
};

export function ActionsForm({ index, isDisabled, isStepRequired, onSubmit }: Props) {
  const { t } = useTranslation();
  const isDemoOTCEnabled = useFeatureFlag('demo-OTC');
  const { setValue, getValues } = useFormContext();

  const fieldName = typeof index === 'number' ? `actions.${index}.` : '';
  const fieldStatus = `${fieldName}status`;

  const isExecuted = isDemoOTCEnabled || getValues(fieldStatus) === actionStatusTypes.executed;

  const actionTypeOptions = [
    {
      label: t('case:actions.type_case_action.maintenance'),
      value: ActionTypes.maintenance,
    },
    {
      label: t('case:actions.type_case_action.operations'),
      value: ActionTypes.operations,
    },
  ];

  const updateStatus = (dueDate?: string, endDate?: string) => {
    const today = dayjs();

    if (endDate) {
      setValue(fieldStatus, actionStatusTypes.executed);
      return;
    }

    if (!dueDate) {
      setValue(fieldStatus, actionStatusTypes.on_track);
      return;
    }

    const isDelayed = dayjs(dueDate).isBefore(today, 'day');
    setValue(fieldStatus, isDelayed ? actionStatusTypes.delayed : actionStatusTypes.on_track);
  };

  return (
    <div
      className={`case-action-form ${
        typeof index === 'number' ? 'case-actions-multiple-cases' : 'case-action-single'
      }`}
    >
      <FormSelect
        isRequired={isStepRequired}
        label={t('case:inputs.type')}
        placeHolderText={t('case:placeholders.select')}
        selectOptionData={actionTypeOptions}
        disabled={isDisabled}
        name={`${fieldName}type_case_action`}
        data-testid="type_case_action"
      />

      <ExternalReference index={index} isDisabled={isDisabled} />

      <FormDatePicker
        label={t('case:inputs.creationDate')}
        dateFormat={t('components:date_filter.date.lts')}
        name={`${fieldName}creation_date`}
        data-testid="creation_date"
        disableOpenPicker
        readOnly
        classname="action-creation-date"
      />

      <FormDatePicker
        name={`${fieldName}due_date`}
        label={
          <div className="case-action-icon">
            {t('case:inputs.dueDateLabel')}
            <Tooltip title={t('case:inputs.dueDateTooltip')}>
              <InfoOutlined />
            </Tooltip>
          </div>
        }
        placeHolderText={t('case:placeholders.pickDate')}
        dateFormat={t('components:date_filter.date.lts')}
        onChangeCallback={(dueDate) => {
          const endDate = getValues(`${fieldName}end_date`);
          updateStatus(dueDate, endDate);
        }}
        data-testid="due_date"
        disabled={isDisabled}
      />

      <FormDatePicker
        name={`${fieldName}end_date`}
        label={
          <div className="case-action-icon">
            {t('case:inputs.executionDateLabel')}
            <Tooltip title={t('case:inputs.executionDateTooltip')}>
              <InfoOutlined />
            </Tooltip>
          </div>
        }
        disabled={isDisabled}
        data-testid="end_date"
        dateFormat={t('components:date_filter.date.lts')}
        onChangeCallback={(endDate) => {
          const dueDate = getValues(`${fieldName}due_date`);
          updateStatus(dueDate, endDate);
        }}
        placeHolderText={
          isExecuted
            ? t('case:placeholders.typeText')
            : t('case:placeholders.availableWithStatus', {
                status: t('case:action.status.executed'),
              })
        }
      />

      <TextEditorWithLabel
        label={t('case:inputs.details')}
        name={`${fieldName}details`}
        placeholder={t('case:placeholders.enterDetails')}
        isDisabled={isDisabled}
        data-testid="details"
        isRequired={isStepRequired}
        onSubmit={onSubmit}
        isModal={false}
      />

      <FormTextField
        className="action-execution-feedback"
        label={t('case:inputs.executionFeedback')}
        rows={2}
        disabled={isDisabled || !isExecuted}
        name={`${fieldName}execution_feedback`}
        data-testid="execution_feedback"
        placeHolderText={
          isExecuted
            ? t('case:placeholders.typeText')
            : t('case:placeholders.availableWithStatus', {
                status: t('case:actions.status.executed'),
              })
        }
      />
    </div>
  );
}
